import React from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { PAGE_LIMIT, MOBILE_MAX_WIDTH, TABLET_MAX_WIDTH } from 'constants/Common';

import { generateRoutePath, isEmpty, isFunction, isObject } from './utils';

export const useBoolean = (initialValue = false) => {
  const [state, _setState] = React.useState(Boolean(initialValue));
  const setState = React.useMemo(() => {
    return {
      true: () => _setState(true),
      false: () => _setState(false),
      toggle: () => _setState((v) => !v),
    };
  }, []);

  React.useDebugValue(state);
  return [state, setState];
};

export const useCompositeState = (initialState = {}) => {
  const [state, _setState] = React.useState(initialState);
  const setState = React.useCallback((...args) => {
    const callback = isFunction(args?.[0]) ? args?.[0] : undefined;
    const object = isObject(args?.[0]) ? args?.[0] : {};
    const spread = !isEmpty(args?.[1]) ? args?.[1] : true;

    _setState((state) => {
      const _object = callback ? callback(state) : object;
      return spread ? { ...state, ..._object } : { ..._object };
    });
  }, []);
  React.useDebugValue(state);
  return [state, setState];
};

export const useRefEffect = (ref, value) => {
  React.useEffect(() => void (ref.current = value), [ref, value]);
};

export const useCustomRef = (initialValue) => {
  const ref = React.useRef(initialValue);
  const refProxy = React.useMemo(
    () => ({
      set: (value) => void (ref.current = value),
      get: () => ref?.current,
    }),
    [],
  );
  return refProxy;
};

export const useCounter = (init = 0) => {
  const [count, set] = React.useState(Number(init));
  const inc = React.useCallback((cb = () => {}) => set((n) => ((n = n > 0 ? ++n : 1), cb(n), n)), []);
  const dec = React.useCallback((cb = () => {}) => set((n) => ((n = n > 0 ? --n : 0), cb(n), n)), []);
  React.useDebugValue(count);
  return [count, inc, dec];
};

export const useLoading = (init) => {
  const [count, inc, dec] = useCounter(Number(Boolean(init)));
  React.useDebugValue(count);
  return [Boolean(count), inc, dec];
};

export const usePagination = (page = 1, limit = PAGE_LIMIT) => {
  const [state, _setState] = React.useState({ page, limit });
  const paramsRef = React.useRef({ page, limit });
  useRefEffect(paramsRef, { page, limit });

  const setPagination = React.useCallback((page, limit) => {
    _setState((state) => ({ ...state, page, limit }));
  }, []);

  const resetPagination = React.useCallback(() => {
    const { page, limit } = paramsRef.current ?? {};
    _setState({ page, limit });
  }, []);

  React.useDebugValue(state);
  return [state?.page, state?.limit, setPagination, resetPagination];
};

export const useList = (list = [], count = 0) => {
  const [state, _setState] = React.useState({ list, count });
  const setList = React.useCallback((list, count) => _setState((state) => ({ ...state, list, count })), []);
  const resetList = React.useCallback(() => _setState(() => ({ list: [], count: 0 })), []);
  React.useDebugValue(state);
  return [state?.list, state?.count, setList, resetList];
};

export const useForm = (initialValues, options) => {
  const [values, setValue] = React.useState(initialValues);
  const [errors, setError] = React.useState({});
  const [touched, setTouched] = React.useState(false);
  const optionsRef = React.useRef(options);
  useRefEffect(options, options);

  const handleChange = React.useCallback((e) => {
    const { name, value } = e?.target ?? e;
    const { validate, transform } = optionsRef.current;

    const _value = transform?.[name]?.(value) ?? value;
    const _error = validate?.[name]?.() ?? undefined;
    setValue((_values) => ({ ..._values, [name]: _value }));
    setError((_errors) => ({ ..._errors, [name]: _error }));
    setTouched((_touched) => ({ ..._touched, [name]: true }));
  }, []);

  const handleCheck = React.useCallback((e) => {
    const { name, checked: value } = e?.target ?? e;
    const { validate, transform } = optionsRef.current;

    const _value = transform?.[name]?.(value) ?? value;
    const _error = validate?.[name]?.() ?? undefined;
    setValue((_values) => ({ ..._values, [name]: _value }));
    setError((_errors) => ({ ..._errors, [name]: _error }));
    setTouched((_touched) => ({ ..._touched, [name]: true }));
  }, []);

  const handleBlur = React.useCallback((e) => {
    const { name } = e?.target ?? e;
    setTouched((_touched) => ({ ..._touched, [name]: true }));
  }, []);

  const output = React.useMemo(
    () => ({ errors, handleBlur, handleChange, handleCheck, touched, values }),
    [errors, handleBlur, handleChange, handleCheck, touched, values],
  );

  return output;
};

export const useWindowSize = () => {
  const [size, setSize] = React.useState([0, 0]);

  React.useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return size;
};

export const useIsMobile = () => {
  const [width, setWidth] = React.useState(window.innerWidth);

  const setSize = React.useCallback(() => {
    setWidth(window.innerWidth);
  }, []);

  React.useEffect(() => {
    setSize();
    window.addEventListener('resize', setSize);
    return () => window.removeEventListener('resize', setSize);
  }, [setSize]);

  const output = React.useMemo(() => {
    const isMobile = width <= MOBILE_MAX_WIDTH;
    const isTablet = width <= TABLET_MAX_WIDTH;
    return [isMobile, isTablet];
  }, [width]);

  React.useDebugValue(output);
  return output;
};

export const usePathname = () => {
  const match = useRouteMatch();
  const pathname = React.useMemo(() => generateRoutePath(match.path)?.split('/')?.pop(), [match.path]);
  React.useDebugValue(pathname);
  return pathname;
};

export const useCurrentType = () => {
  const location = useLocation();
  const currentType = location.pathname;
  React.useDebugValue(currentType);
  return currentType;
};
